import { useMutation } from '@apollo/client';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import { useTranslations } from '@vocab/react';
import { useAtom, useAtomValue } from 'jotai';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { Country } from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import type { UseCase } from '../../entities/enums';
import {
  CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
  CREATE_STRIPE_PAYMENT_INTENT,
} from '../../graphql';
import { CREATE_SEEK_PAYMENT_INTENT } from '../../graphql/Mutations';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import translations from '../../locales.vocab';
import type { PaymentStatus, Invoice } from '../../types';
import { handleOrderErrorPayload } from '../../utils/handleOrderError';
import {
  CreditCardFormNotValidError,
  PaymentCardUnsupportedError,
  PaymentConfirmationError,
  PaymentDefaultError,
} from '../Errors/customErrors';
import { hideCreditCardAtom } from '../Maintenance/MaintenanceStore';
import { selectedCardBrandAtom } from '../Surcharges/SurchargeStore';
import useBrandSurcharge from '../Surcharges/useBrandSurcharge';

import { formValidationErrorsAtom, isSaveCardAtom } from './CreditCardStore';
import { checkSupportedCards } from './cards';
import type { CreditCardFormValidationError } from './creditcardTypes';
import { getApiCreditCardBrand } from './getCreditCardBrand';

interface InvoiceInputs {
  accountId: string;
  customerId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  orderId: string;
  advertiserId?: string;
  onSaveCardOptionChanged?: (shouldSave: boolean) => void;
}

interface GenericInputs {
  seekPaymentIntentId: string;
  site: string;
}

export interface UseCreditCardFormInput<T> {
  currency: Currency;
  country: Country;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
  genericInputs: T extends 'GENERIC' ? GenericInputs : undefined;
}

const useCreditCardForm = ({
  country,
  invoiceInputs = {
    accountId: '',
    customerId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    advertiserId: '',
    orderId: '',
    onSaveCardOptionChanged: () => {},
  },
  genericInputs = {
    seekPaymentIntentId: '',
    site: '',
  },
}: UseCreditCardFormInput<UseCase>) => {
  const { accountId, customerId, prn, invoice, email, totalForPaymentIntent } =
    invoiceInputs;
  const { orderId, onSaveCardOptionChanged } = cajaInputs;
  const { seekPaymentIntentId, site } = genericInputs;
  const stripe = useStripe();
  const elements = useElements();
  const { sendViewAlertViewedEvent, sendLinkSaveCardCheckboxEvent } =
    useAnalytics();

  const { t } = useTranslations(translations);

  const [createStripePaymentIntent] = useMutation(CREATE_STRIPE_PAYMENT_INTENT);
  const [createSeekPaymentIntent] = useMutation(CREATE_SEEK_PAYMENT_INTENT);

  const [createStripePaymentIntentForInvoice] = useMutation(
    CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
  );

  const { idempotencyKey, generateNewIdempotencyKey } =
    useGenerateIdempotencyKey();

  const [isSaveCard, setIsSaveCard] = useAtom(isSaveCardAtom);
  const [formValidationErrors, setFormValidationErrors] = useAtom(
    formValidationErrorsAtom,
  );
  const selectedCardBrand = useAtomValue(selectedCardBrandAtom);
  const isMaintenance = useAtomValue(hideCreditCardAtom);

  const { pickBrand } = useBrandSurcharge();

  const isDisplaySurcharge = ['AU', 'NZ'].includes(country);

  const supportedCreditCards = checkSupportedCards(country);

  const setFormError = (
    field: CreditCardFormValidationError,
    message: string,
  ) => {
    setFormValidationErrors({
      ...formValidationErrors,
      [field]: message,
    });
  };

  const onCreditCardFormChange = (
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent
      | React.BaseSyntheticEvent,
    id: string,
  ) => {
    if (!stripe || !elements) {
      return;
    }
    generateNewIdempotencyKey();

    switch (id) {
      case 'card-number':
        let errMsg = '';
        const cardNumberEvent = event as StripeCardNumberElementChangeEvent;

        const brand = cardNumberEvent.brand.toLowerCase();

        if (
          (cardNumberEvent.error && cardNumberEvent.error.message) ||
          cardNumberEvent?.empty ||
          !supportedCreditCards.includes(brand)
        ) {
          errMsg = t('stripe.form.cardnumber.error');

          if ((brand === 'amex' || brand === 'jcb') && country === 'TH') {
            errMsg = t('invalid.card.th.error');
          }
        } else {
          errMsg = '';
        }

        setFormError('card-number', errMsg);
        pickBrand(getApiCreditCardBrand(cardNumberEvent.brand));
        break;
      case 'card-expiry':
        let errMsgExpiry = '';
        const cardExpiryEvent = event as StripeCardExpiryElementChangeEvent;
        if (
          (cardExpiryEvent.error && cardExpiryEvent.error.message) ||
          cardExpiryEvent.empty
        ) {
          errMsgExpiry = t('stripe.form.cardexpiry.error');
        } else {
          errMsgExpiry = '';
        }
        setFormError('card-expiry', errMsgExpiry);
        break;
      case 'card-cvc':
        let errMsgCvc = '';
        const cardCvcEvent = event as StripeCardCvcElementChangeEvent;
        if (
          (cardCvcEvent.error && cardCvcEvent.error.message) ||
          cardCvcEvent.empty
        ) {
          errMsgCvc = t('stripe.form.cardcvc.error');
        } else {
          errMsgCvc = '';
        }

        setFormError('card-cvc', errMsgCvc);
        break;
      case 'card-is-save':
        const cardIsSaveEvent = event as React.BaseSyntheticEvent;
        if (onSaveCardOptionChanged) {
          onSaveCardOptionChanged(cardIsSaveEvent.target.checked as boolean);
        }
        setIsSaveCard(cardIsSaveEvent.target.checked as boolean);
        sendLinkSaveCardCheckboxEvent(
          cardIsSaveEvent.target.checked as boolean,
        );
        break;
      default:
        break;
    }
  };

  const isCreditCardFormValid = (): boolean => {
    let result = true;

    const fields: CreditCardFormValidationError[] = [
      'card-cvc',
      'card-expiry',
      'card-number',
    ];

    for (const field of fields) {
      // if field is null, it means user has not touched the field
      if (formValidationErrors[field] === null) {
        switch (field) {
          case 'card-number':
            formValidationErrors[field] = t('stripe.form.cardnumber.error');
            break;
          case 'card-expiry':
            formValidationErrors[field] = t('stripe.form.cardexpiry.error');
            break;
          case 'card-cvc':
            formValidationErrors[field] = t('stripe.form.cardcvc.error');
            break;
          default:
            break;
        }
      }
      // if field is '', it means user has touched the field and it is valid
      if (formValidationErrors[field] !== '') {
        result = false;
      }
    }

    if (!result) {
      setFormValidationErrors(formValidationErrors);
    }

    return result;
  };

  const submitCreditCardPayment = async () => {
    if (!stripe || !elements) {
      throw new PaymentDefaultError('Stripe or Elements is not loaded');
    }

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    // Need to check if form is valid before checking selectedCardBrand === 'UNKNOWN'
    // because this will trigger the error message in credit card form to show up
    if (!isCreditCardFormValid()) {
      sendViewAlertViewedEvent(
        [
          {
            type: 'banner',
            title:
              'Your card details are incomplete or invalid. Please check you have entered the correct details and try again.',
            messageCode: 'credit_card_form_error',
          },
        ],
        'CreditCard',
      );
      throw new CreditCardFormNotValidError();
    }

    if (selectedCardBrand === 'UNKNOWN') {
      throw new PaymentCardUnsupportedError('Unsupported credit card brand');
    }

    const intentResponse = await createStripePaymentIntent({
      variables: {
        input: {
          creditCardBrand: selectedCardBrand,
          orderId,
          paymentMethod: 'card',
          saveCard: isSaveCard,
          idempotencyKey,
        },
      },
    });

    const createPaymentIntentPayload =
      intentResponse.data?.createStripePaymentIntent;

    if (!createPaymentIntentPayload) {
      throw new PaymentDefaultError('Unexpected undefined response payload');
    }

    // fail early if payload is error
    if (
      !['PaymentIntentPayload', 'StripePaymentIntentPayload'].includes(
        createPaymentIntentPayload.__typename,
      )
    ) {
      handleOrderErrorPayload(
        createPaymentIntentPayload.__typename,
        createPaymentIntentPayload.message,
      );

      throw new PaymentDefaultError(createPaymentIntentPayload.message);
    }

    // Scalability: skip payment gateway call
    if (
      createPaymentIntentPayload.clientSecret === 'test-record-clientsecret'
    ) {
      return {
        paymentIntentId: createPaymentIntentPayload.id,
        status: 'succeeded' as PaymentStatus,
      };
    }

    const confirmationResponse = await stripe.confirmCardPayment(
      createPaymentIntentPayload.clientSecret,
      {
        payment_method: {
          card: elements.getElement(
            CardNumberElement,
          ) as StripeCardNumberElement,
        },
      },
    );

    if (confirmationResponse.error) {
      throw new PaymentConfirmationError(
        confirmationResponse.error.message ?? 'Stripe error',
        confirmationResponse.error.type,
        confirmationResponse.error.code,
        confirmationResponse.error.decline_code,
      );
    }

    return {
      paymentIntentId: confirmationResponse.paymentIntent?.id,
      status: confirmationResponse.paymentIntent?.status,
    };
  };

  const submitCreditCardPaymentForInvoices = async () => {
    if (!stripe || !elements) {
      throw new PaymentDefaultError('Stripe or Elements is not loaded');
    }

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    // Need to check if form is valid before checking selectedCardBrand === 'UNKNOWN'
    // because this will trigger the error message in credit card form to show up
    if (!isCreditCardFormValid()) {
      sendViewAlertViewedEvent(
        [
          {
            type: 'banner',
            title:
              'Your card details are incomplete or invalid. Please check you have entered the correct details and try again.',
            messageCode: 'credit_card_form_error',
          },
        ],
        'CreditCard',
      );
      throw new CreditCardFormNotValidError();
    }

    if (selectedCardBrand === 'UNKNOWN') {
      throw new PaymentCardUnsupportedError('Unsupported credit card brand');
    }

    // temporarily update id to reference, will change it after BE update
    const invoices = invoice!.map(({ id, ...rest }) => ({
      reference: id,
      ...rest,
    }));

    const intentResponse = await createStripePaymentIntentForInvoice({
      variables: {
        seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
        seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
        paymentMethod: 'card',
        totalForPaymentIntent,
        creditCardBrand: selectedCardBrand,
        invoices,
        email,
        prn,
        saveCard: isSaveCard,
        idempotencyKey,
      },
    });

    const createPaymentIntentPayload =
      intentResponse?.data?.createStripePaymentIntentForInvoice;

    if (!createPaymentIntentPayload) {
      throw new PaymentDefaultError('Unexpected undefined response payload');
    }

    // fail early if payload is error
    if (
      !['StripePaymentIntentPayload'].includes(
        createPaymentIntentPayload.__typename,
      )
    ) {
      throw new PaymentDefaultError(createPaymentIntentPayload.message);
    }

    // Scalability: skip payment gateway call
    if (
      createPaymentIntentPayload.clientSecret === 'test-record-clientsecret'
    ) {
      return {
        status: 'succeeded' as PaymentStatus,
      };
    }

    const confirmationResponse = await stripe.confirmCardPayment(
      createPaymentIntentPayload.clientSecret,
      {
        payment_method: {
          card: elements.getElement(
            CardNumberElement,
          ) as StripeCardNumberElement,
        },
      },
    );

    if (confirmationResponse.error) {
      throw new PaymentConfirmationError(
        confirmationResponse.error.message ?? 'Stripe error',
        confirmationResponse.error.type,
        confirmationResponse.error.code,
        confirmationResponse.error.decline_code,
      );
    }

    return {
      paymentIntentId: confirmationResponse.paymentIntent?.id,
      status: confirmationResponse.paymentIntent?.status,
    };
  };

  const submitCreditCardPaymentForGeneric = async () => {
    if (!stripe || !elements) {
      throw new PaymentDefaultError('Stripe or Elements is not loaded');
    }

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    // Need to check if form is valid before checking selectedCardBrand === 'UNKNOWN'
    // because this will trigger the error message in credit card form to show up
    if (!isCreditCardFormValid()) {
      sendViewAlertViewedEvent(
        [
          {
            type: 'banner',
            title:
              'Your card details are incomplete or invalid. Please check you have entered the correct details and try again.',
            messageCode: 'credit_card_form_error',
          },
        ],
        'CreditCard',
      );
      throw new CreditCardFormNotValidError();
    }

    if (selectedCardBrand === 'UNKNOWN') {
      throw new PaymentCardUnsupportedError('Unsupported credit card brand');
    }

    const intentResponse = await createSeekPaymentIntent({
      variables: {
        seekPaymentIntentId,
        paymentMethod: 'card',
        country,
        site,
      },
    });

    const createPaymentIntentPayload = intentResponse.data?.createPaymentIntent;

    if (!createPaymentIntentPayload) {
      throw new PaymentDefaultError('Unexpected undefined response payload');
    }

    // Scalability: skip payment gateway call
    if (
      createPaymentIntentPayload.clientSecret === 'test-record-clientsecret'
    ) {
      return {
        paymentIntentId: createPaymentIntentPayload.id,
        status: 'succeeded' as PaymentStatus,
      };
    }

    const confirmationResponse = await stripe.confirmCardPayment(
      createPaymentIntentPayload.clientSecret,
      {
        payment_method: {
          card: elements.getElement(
            CardNumberElement,
          ) as StripeCardNumberElement,
        },
      },
    );

    if (confirmationResponse.error) {
      throw new PaymentConfirmationError(
        confirmationResponse.error.message ?? 'Stripe error',
        confirmationResponse.error.type,
        confirmationResponse.error.code,
        confirmationResponse.error.decline_code,
      );
    }

    return {
      paymentIntentId: confirmationResponse.paymentIntent?.id,
      status: confirmationResponse.paymentIntent?.status,
    };
  };

  return {
    onCreditCardFormChange,
    submitCreditCardPayment,
    submitCreditCardPaymentForInvoices,
    submitCreditCardPaymentForGeneric,
    formValidationErrors, // for testing to keep track of form errors
    isDisplaySurcharge,
  };
};

export default useCreditCardForm;
